import moment from "moment";

/**
 * Format Unix timestamp to readable date-time string
 */
const formatUTCTimeWithSec = (unixTime) => {
  return moment(unixTime).format("YYYY-MM-DD HH:mm:ss");
};

/**
 * Converts nested production JSON data to hierarchical CSV format
 * Each device gets its full parent context (production + item data)
 */
export const convertProductionToCSV = (responseData) => {
  // Validate input
  if (!responseData) {
    throw new Error("Production data is null or undefined");
  }

  // Get the data array
  const dataArray = responseData.data || responseData;

  if (!Array.isArray(dataArray)) {
    console.error("Invalid data structure:", responseData);
    throw new Error("Expected data to be an array");
  }

  const rows = [];

  // Row 1: Main header groups (visual grouping, not functional in CSV)
  rows.push([
    "Production",
    "",
    "",
    "",
    "Production Item(s)",
    "",
    "",
    "Production Item Device(s)",
    "",
    "",
    "",
    "",
  ]);

  // Row 2: Column headers
  rows.push([
    "No",
    "Name",
    "Description",
    "Production Time",
    "Item No",
    "Product",
    "Hardware",
    "Device No",
    "MAC Address",
    "Node Type",
    "Door",
    "Devices",
  ]);

  // Data rows
  dataArray.forEach((production) => {
    if (
      !production.Production_export_items ||
      production.Production_export_items.length === 0
    ) {
      // Production with no items
      rows.push([
        production.ProductionNo || "",
        production.ProductionName || "",
        production.ProductionDesc || "",
        production.ProductionTime
          ? formatUTCTimeWithSec(production.ProductionTime)
          : "",
        "No items",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
      ]);
      return;
    }

    production.Production_export_items.forEach((item, itemIndex) => {
      if (
        !item.Production_item_devices ||
        item.Production_item_devices.length === 0
      ) {
        // Item with no devices - show production and item data
        rows.push([
          production.ProductionNo || "",
          production.ProductionName || "",
          production.ProductionDesc || "",
          production.ProductionTime
            ? formatUTCTimeWithSec(production.ProductionTime)
            : "",
          item.ProductionItemNo || "",
          item.Product || "",
          item.Hardware || "",
          "No devices",
          "",
          "",
          "",
          "",
        ]);
        return;
      }

      // For each device, include full production and item context
      item.Production_item_devices.forEach((device, deviceIndex) => {
        rows.push([
          // Production info (repeated for every device)
          production.ProductionNo || "",
          production.ProductionName || "",
          production.ProductionDesc || "",
          production.ProductionTime
            ? formatUTCTimeWithSec(production.ProductionTime)
            : "",
          // Item info (repeated for every device in the item)
          item.ProductionItemNo || "",
          item.Product || "",
          item.Hardware || "",
          // Device info (unique for each device)
          device.DeviceNo || "",
          device.Mac || "",
          device.NodeType || "",
          device.Type || "",
          device.Devices || "",
        ]);
      });
    });
  });

  // Convert to CSV string
  const csvContent = rows
    .map((row) =>
      row
        .map((cell) => {
          // Escape cells that contain commas, quotes, or newlines
          const cellStr = String(cell);
          if (
            cellStr.includes(",") ||
            cellStr.includes('"') ||
            cellStr.includes("\n")
          ) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        })
        .join(","),
    )
    .join("\n");

  return csvContent;
};

/**
 * Download CSV file
 */
export const downloadProductionCSV = (
  productionData,
  filename = "production_report.csv",
) => {
  const csvContent = convertProductionToCSV(productionData);

  // Create blob and download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up
  URL.revokeObjectURL(url);
};
